import { convertKeysToCamelCase } from "@/lib/format-data";
import { parseError } from "@/lib/parse-error";
import { getTimezone } from "@/lib/timezone";

import type {
  BlastAnalyticsResponse,
  BlastCreatePayload,
  BlastDetail,
  BlastDuplicateResponse,
  BlastEngagementAnalytics,
  BlastFetchParams,
  BlastMessageItem,
  BlastPreviewRequest,
  BlastPreviewResponse,
  BlastRecipientPreview,
  BlastRecipientSelection,
  BlastSummary,
  BlastTestSendRequest,
  CostEstimateRequest,
  CostEstimateResponse,
  EmailPreviewRequest,
  EmailPreviewResponse,
  EmailVerificationSummary,
  RecipientVerificationStatus,
  StartVerificationResponse,
} from "@/types/blast";
import type {
  CohortCreatePayload,
  CohortDetail,
  CohortDuplicateCheckResponse,
  CohortExportPatient,
  CohortFetchParams,
  CohortFilters,
  CohortOption,
  CohortPatientCountResponse,
  CohortSummary,
  CohortUpdatePayload,
  CustomContactsPreviewResponse,
} from "@/types/cohort";
import type { Page } from "@/types/pagination";
import type { PatientFetchParams } from "@/types/patient";

import { requestData } from "@/api/api-config";
import { serializeBlastAnalyticsFilters } from "@/features/analytics/filters/special-families";

// ─── Blasts ──────────────────────────────────────────────────────────────────

export async function fetchBlasts(
  params: BlastFetchParams,
): Promise<Page<BlastSummary> | null> {
  return await requestData<Page<BlastSummary>>("comms/blasts", "get", {
    searchParams: params as Record<string, string>,
  });
}

export async function getBlast(blastId: string): Promise<BlastDetail | null> {
  return await requestData<BlastDetail>(`comms/blasts/${blastId}`, "get", {
    searchParams: { tz: getTimezone() },
  });
}

export async function createBlast(
  payload: BlastCreatePayload,
): Promise<BlastSummary | null> {
  try {
    return await requestData<BlastSummary>(
      "comms/blasts",
      "post",
      {
        json: payload,
      },
      false,
    );
  } catch (error: any) {
    throw new Error(await parseError(error, "Failed to send blast"));
  }
}

export interface BlastMessagesFilters {
  filter?: string;
  recipient?: string;
  sentAfter?: string;
  sentBefore?: string;
}

export async function getBlastMessages(
  blastId: string,
  page: number = 1,
  perPage: number = 25,
  filters: BlastMessagesFilters = {},
): Promise<Page<BlastMessageItem> | null> {
  const searchParams: Record<string, string> = {
    page: String(page),
    perPage: String(perPage),
  };
  if (filters.filter) searchParams.filter = filters.filter;
  if (filters.recipient) searchParams.recipient = filters.recipient;
  if (filters.sentAfter) searchParams.sent_after = filters.sentAfter;
  if (filters.sentBefore) searchParams.sent_before = filters.sentBefore;
  return await requestData<Page<BlastMessageItem>>(
    `comms/blasts/${blastId}/messages`,
    "get",
    { searchParams },
  );
}

export async function retryBlast(
  blastId: string,
): Promise<BlastSummary | null> {
  try {
    return await requestData<BlastSummary>(
      `comms/blasts/${blastId}/retry`,
      "post",
      undefined,
      false,
    );
  } catch (error: any) {
    throw new Error(await parseError(error, "Failed to retry blast"));
  }
}

export async function stopBlast(blastId: string): Promise<BlastSummary | null> {
  try {
    return await requestData<BlastSummary>(
      `comms/blasts/${blastId}/stop`,
      "post",
      undefined,
      false,
    );
  } catch (error: any) {
    throw new Error(await parseError(error, "Failed to stop blast"));
  }
}

export async function rescheduleBlast(
  blastId: string,
  scheduledFor: Date,
): Promise<BlastSummary | null> {
  try {
    return await requestData<BlastSummary>(
      `comms/blasts/${blastId}/schedule`,
      "put",
      { json: { scheduledFor: scheduledFor.toISOString() } },
      false,
    );
  } catch (error: any) {
    throw new Error(await parseError(error, "Failed to reschedule blast"));
  }
}

export async function cancelScheduledBlast(
  blastId: string,
): Promise<BlastSummary | null> {
  try {
    return await requestData<BlastSummary>(
      `comms/blasts/${blastId}/cancel`,
      "post",
      undefined,
      false,
    );
  } catch (error: any) {
    throw new Error(await parseError(error, "Failed to cancel blast"));
  }
}

export async function previewBlast(
  payload: BlastPreviewRequest,
): Promise<BlastPreviewResponse | null> {
  const result = await requestData<BlastPreviewResponse>(
    "comms/blasts/preview",
    "post",
    { json: payload },
  );
  return result
    ? {
        ...result,
        waitlistRecipients: result.waitlistRecipients ?? [],
        duplicatePhoneConflicts: result.duplicatePhoneConflicts ?? [],
      }
    : null;
}

export async function previewBlastRecipients(
  payload: BlastPreviewRequest,
  page: number = 1,
  perPage: number = 25,
  search?: string,
  sendableOnly: boolean = false,
  includeNotOptedIn: boolean = false,
): Promise<{ data: BlastRecipientPreview[]; total: number } | null> {
  const searchParams: Record<string, string> = {
    page: String(page),
    perPage: String(perPage),
  };
  if (search) searchParams.search = search;
  if (sendableOnly) searchParams.sendableOnly = "true";
  if (includeNotOptedIn) searchParams.includeNotOptedIn = "true";
  return await requestData("comms/blasts/preview-recipients", "post", {
    json: payload,
    searchParams,
  });
}

export async function estimateCost(
  payload: CostEstimateRequest,
): Promise<CostEstimateResponse | null> {
  return await requestData<CostEstimateResponse>(
    "comms/blasts/estimate-cost",
    "post",
    { json: payload },
  );
}

export interface BlastAnalyticsFilterParams {
  blastNames?: string[];
  blastMediums?: string[];
  blastTypes?: string[];
}

export async function getBlastAnalytics(
  from: string,
  to: string,
  filters?: BlastAnalyticsFilterParams,
  timezone: string = getTimezone(),
): Promise<BlastAnalyticsResponse | null> {
  const raw = await requestData<Record<string, unknown>>(
    "comms/blasts/analytics",
    "get",
    {
      searchParams: serializeBlastAnalyticsFilters(
        {
          startDate: from,
          endDate: to,
          blastNames: filters?.blastNames,
          blastMediums: filters?.blastMediums,
          blastTypes: filters?.blastTypes,
        },
        timezone,
      ),
    },
  );
  if (!raw) return null;
  return convertKeysToCamelCase(raw) as BlastAnalyticsResponse;
}

export async function testSendBlast(
  payload: BlastTestSendRequest,
): Promise<{ success: boolean; channel: string; recipient: string } | null> {
  try {
    return await requestData<{
      success: boolean;
      channel: string;
      recipient: string;
    }>(
      "comms/blasts/test-send",
      "post",
      {
        json: payload,
      },
      false,
    );
  } catch (error: any) {
    throw new Error(await parseError(error, "Failed to send test"));
  }
}

export async function getBlastEngagement(
  blastId: string,
): Promise<BlastEngagementAnalytics | null> {
  return await requestData<BlastEngagementAnalytics>(
    `comms/blasts/${blastId}/analytics`,
    "get",
  );
}

export async function previewEmail(
  payload: EmailPreviewRequest,
): Promise<EmailPreviewResponse | null> {
  return await requestData<EmailPreviewResponse>(
    "comms/blasts/preview-email",
    "post",
    { json: payload },
  );
}

export interface BlastFileUploadResult {
  gcsPath: string;
  signedUrl: string;
  filename: string;
}

export async function uploadBlastFiles(
  files: File[],
  channel: "SMS" | "EMAIL",
): Promise<BlastFileUploadResult[] | null> {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file);
  }
  const raw = await requestData<Record<string, unknown>[]>(
    "comms/blasts/upload",
    "post",
    { body: formData, searchParams: { channel } },
  );
  if (!raw) return null;
  return convertKeysToCamelCase(raw) as BlastFileUploadResult[];
}

export async function addBlastRecipients(
  blastId: string,
  patientIds: string[],
): Promise<BlastSummary | null> {
  try {
    return await requestData<BlastSummary>(
      `comms/blasts/${blastId}/add-recipients`,
      "post",
      { json: { patientIds } },
      false,
    );
  } catch (error: any) {
    throw new Error(await parseError(error, "Failed to add recipients"));
  }
}

export async function duplicateBlast(
  blastId: string,
): Promise<BlastDuplicateResponse | null> {
  return await requestData<BlastDuplicateResponse>(
    `comms/blasts/${blastId}/duplicate`,
    "post",
  );
}

export async function fetchCohortPatientCount(
  cohortIds: string[],
): Promise<CohortPatientCountResponse | null> {
  const tz = getTimezone();
  // A GET so ky retries the 503 the API returns while a just-saved cohort is
  // still replicating to the read replica.
  return await requestData<CohortPatientCountResponse>(
    "comms/cohorts/patient-count",
    "get",
    { searchParams: { cohortIds, tz } },
  );
}

// ─── Email verification ────────────────────────────────────────────────────

export async function fetchVerificationSummary(
  selection: BlastRecipientSelection,
): Promise<EmailVerificationSummary | null> {
  return await requestData<EmailVerificationSummary>(
    "comms/email-verification/summary",
    "post",
    { json: selection },
  );
}

export async function startVerification(
  selection: BlastRecipientSelection,
): Promise<StartVerificationResponse | null> {
  try {
    return await requestData<StartVerificationResponse>(
      "comms/email-verification/start",
      "post",
      { json: selection },
      false,
    );
  } catch (error: any) {
    throw new Error(await parseError(error, "Failed to start verification"));
  }
}

// ─── Cohorts ─────────────────────────────────────────────────────────────────

export async function fetchCohorts(
  params: CohortFetchParams,
): Promise<Page<CohortSummary> | null> {
  return await requestData<Page<CohortSummary>>("comms/cohorts", "get", {
    searchParams: params as Record<string, string>,
  });
}

export async function fetchCohortOptions(): Promise<CohortOption[] | null> {
  return await requestData<CohortOption[]>("comms/cohorts/options", "get", {});
}

export async function getCohort(
  cohortId: string,
): Promise<CohortDetail | null> {
  const tz = getTimezone();
  return await requestData<CohortDetail>(`comms/cohorts/${cohortId}`, "get", {
    searchParams: { tz, includePatientCount: false },
  });
}

export async function createCohort(
  payload: CohortCreatePayload,
  senderId: string,
): Promise<CohortSummary | null> {
  return await requestData<CohortSummary>("comms/cohorts", "post", {
    json: { ...payload, senderId },
  });
}

export async function updateCohort(
  cohortId: string,
  payload: CohortUpdatePayload,
): Promise<CohortSummary | null> {
  return await requestData<CohortSummary>(`comms/cohorts/${cohortId}`, "put", {
    json: payload,
  });
}

export async function deleteCohort(cohortId: string): Promise<boolean | null> {
  return await requestData<boolean>(`comms/cohorts/${cohortId}`, "delete");
}

export async function checkCohortDuplicate(
  filterCriteria: CohortFilters,
  excludeCohortId?: string,
): Promise<CohortDuplicateCheckResponse | null> {
  return await requestData<CohortDuplicateCheckResponse>(
    "comms/cohorts/check-duplicate",
    "post",
    { json: { filterCriteria, excludeCohortId } },
  );
}

export async function previewCohortCount(
  filterCriteria: CohortFilters,
  tz?: string,
): Promise<CohortPatientCountResponse | null> {
  return await requestData<CohortPatientCountResponse>(
    "comms/cohorts/preview-count",
    "post",
    {
      json: { filterCriteria },
      searchParams: tz ? { tz } : undefined,
    },
  );
}

export async function previewCustomContacts(
  contacts: string[],
): Promise<CustomContactsPreviewResponse | null> {
  return await requestData<CustomContactsPreviewResponse>(
    "comms/cohorts/preview-custom-contacts",
    "post",
    { json: { contacts } },
    // catchError=false so an HTTP failure throws instead of resolving null.
    // The preview query relies on React Query's isError to surface its error
    // state; a swallowed null would leave the dialog stuck in "Checking…".
    false,
  );
}

export async function getCohortPatients(
  cohortId: string,
  page: number = 1,
  perPage: number = 25,
  channel?: string,
  isMarketing?: boolean,
): Promise<{
  data: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phoneNumber: string | null;
    blastStatus: string | null;
    verificationStatus?: RecipientVerificationStatus | null;
  }>;
  total: number;
} | null> {
  const searchParams: Record<string, string> = {
    page: String(page),
    perPage: String(perPage),
  };
  searchParams.tz = getTimezone();
  if (channel) searchParams.channel = channel;
  if (isMarketing !== undefined) searchParams.isMarketing = String(isMarketing);
  return await requestData(`comms/cohorts/${cohortId}/patients`, "get", {
    searchParams,
  });
}

export async function getCohortExportPatients(
  cohortId: string,
  page: number,
  perPage: number,
  filters?: PatientFetchParams,
): Promise<{
  data: CohortExportPatient[];
  total: number;
} | null> {
  return await requestData<{ data: CohortExportPatient[]; total: number }>(
    `comms/cohorts/${cohortId}/export`,
    "get",
    {
      // The export pages on its own; the table's page must not leak in.
      searchParams: { ...filters, page, perPage, tz: getTimezone() },
    },
    // catchError=false so an HTTP failure throws instead of resolving null.
    // The fetch loop reads null as "no more pages" — a swallowed error on a
    // middle page would silently write a truncated export file.
    false,
  );
}
